import { CheckboxValueType } from 'antd/es/checkbox/Group';
import React from 'react';
import { EDISPUTE_STATUS, EStatus, ServerMeta } from './common';

// Decision Tree Types
export interface IDecisionRuleConfig {
    id: string;
    assessmentUuid: string;
    assessmentId: string | number;
    assessmentName: string;
    configName: string;
    description: string;
    version: string;
    isActive: boolean;
    isDefault: boolean;
    rules: IDecisionRule[];
    defaultResult: IDecisionResult;
    operatorId?: string;
    operatorName?: string;
    createdAt: string;
    updatedAt: string;
    lastUsedAt?: string;
    usageCount: number;
}

export interface IDecisionRule {
    id: string;
    configId: string;
    name: string;
    description: string;
    conditions: IDecisionCondition[];
    result: IDecisionResult;
    priority: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface IDecisionCondition {
    id: string;
    ruleId: string;
    questionType: 'questionFrom' | 'engagementQuestion' | 'mbiQuestion';
    questionId: string;
    questionTitle?: string;
    operator:
        | 'equals'
        | 'not_equals'
        | 'contains'
        | 'not_contains'
        | 'greater_than'
        | 'less_than'
        | 'in'
        | 'not_in'
        | 'between'
        | 'regex';
    value: any;
    logicalOperator?: 'AND' | 'OR';
    createdAt: string;
    updatedAt: string;
}

export interface IDecisionResult {
    id: string;
    ruleId: string;
    type: 'not_suitable' | 'video_visit' | 'text_visit' | 'continue' | 'custom';
    title: string;
    description: string;
    message: string;
    ctaText: string;
    ctaUrl: string;
    createdAt: string;
    updatedAt: string;
}

export interface IDecisionEngineConfig {
    id: string;
    assessmentId: string;
    name: string;
    description: string;
    rules: IDecisionRule[];
    defaultResult: IDecisionResult;
    version: string;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
}

export interface IUserAnswers {
    questionFromAnswers: Record<string, any>;
    engagementAnswers: Record<string, any>;
    mbiAnswers: Record<string, any>;
    metadata: {
        sessionId: string;
        timestamp: string;
        assessmentId: string;
        userAgent: string;
    };
}

export interface IDecisionExecutionResult {
    id: string;
    configId: string;
    assessmentUuid: string;
    result: IDecisionResult;
    matchedRule?: IDecisionRule;
    answersUsed: string[];
    executionTime: number;
    timestamp: string;
    success: boolean;
    error?: string;
}

// Assessment Rules Types
export interface IAssessmentRule {
    id?: number;
    assessmentId: number;
    assessmentUuid: string;
    assessmentRuleDataJson: string;
    createdAt?: string;
    updatedAt?: string;
}

export interface IAssessmentRuleInput {
    id?: number;
    assessmentId: number;
    assessmentUuid: string;
    assessmentRuleDataJson?: string;
    assessmentFeaturesJson?: string;
}

export interface IAssessmentRuleBatchInput {
    promptManagement: string;
    assessmentId: number;
    reqs: IAssessmentRuleInput[];
}

export interface IAssessmentRuleResponse {
    id: number;
    assessmentId: number;
    assessmentUuid: string;
    assessmentRuleDataJson: string;
    createdAt: string;
    updatedAt: string;
}

export interface IAssessmentRuleCountResponse {
    count: number;
}

export interface IRuleConfigLog {
    id: string;
    configId: string;
    action: 'create' | 'update' | 'delete' | 'activate' | 'deactivate' | 'copy' | 'execute';
    operatorId: string;
    operatorName: string;
    details: string;
    timestamp: string;
}

export interface IRuleConfigStats {
    configId: string;
    totalRules: number;
    activeRules: number;
    totalExecutions: number;
    successRate: number;
    averageExecutionTime: number;
    mostUsedRule?: string;
    lastExecutionAt?: string;
    createdAt: string;
    updatedAt: string;
}

export enum EProgressForProvider {
    PENDING_REVIEW = 'Pending Review',
    FOLLOW_UP_WITH_PROVIDER = 'Follow-up with Provider',
    APPLICATION_APPROVED = 'Application approved',
    APPLICATION_ON_HOLD = 'Application On-Hold',
    BP_ACCOUNT_ENTERED = 'BP Account Entered',
}

export interface GetOpsListInput {
    nameCond?: string;
    auditStatusList?: Array<CheckboxValueType>;
    applicationStatusList?: Array<CheckboxValueType>;
    orderKey?: string;
    orderType?: string;
    current: number;
    size: number;
}

export interface GetListCommon {
    size: number;
    current: number;
}

export interface GetOpsUniprofileInput {
    nameCond?: string;
    providerId?: string;
    auditStatusList?: Array<string>;
    uniprofileStatusList?: Array<string>;
    orderKey?: string;
    orderType?: string;
    assignee?: string;
    current: number;
    size: number;
    providerPayPlanTypeList?: string[];
    payStatusList?: string[];
    archiveStatusList?: string[];
    sourceList?: string[];
    couponNameList?: string[];
    promotionCodeList?: string[];
    contractVersion?: string[]; // 2024-07-30 后端接收的参数改为了string[]
    updateFlagList?: boolean[];
    productList?: string[];
    uidPublic?: string;
    latestLaunchAt?: string;
    firstLaunchAt?: string;
    productsInterestedList?: string[];
    groupProvider?: boolean;
}

export interface GetOpsDetailInput {
    channelId: number;
    providerId?: number;
    step: string;
    account?: string;
    password?: string;
    googleTitle?: string;
}

export interface GetOpsUniprfoleDetailInput {
    channelId?: number;
    providerId: number | string;
    step: string;
}

export interface SelectProviderChannelPhotoInput {
    providerId: number;
    selectedPhoto: string;
}

export interface ProviderChannelPhotoInput {
    providerId: number;
    selectedPhoto: string;
}

export interface GetProfileReviewListInput {
    channelId: number;
    current: number;
    providerId: number;
    size: number;
}

export interface UpdateProviderChannelAuditStatusInput {
    channelId: number;
    providerId: number;
    rejectReason?: string;
    passed: boolean;
    website?: string;
    googleTitle?: string;
    channelMappingAccount?: string;
    channelMappingPassword?: string;
}

export interface UpdateUniprofileAuditStatusInput {
    providerId: number;
    rejectReason?: string;
    passed: boolean;
}

export interface UpdateOpsChannelWebsiteInput {
    channelId: number;
    providerId: number;
    website: string;
    channelMappingAccount?: string;
    channelMappingPassword?: string;
    googleTitle?: string;
}

export interface ProviderChannelAuditGoLiveInput {
    channelId: number;
    providerId: number;
}

export interface ProviderReview {
    providerId: number;
    channelId: number;
    reviewerName: string;
    reviews: string;
    ratings: number;
    verified: boolean;
    anonymous: boolean;
}

export interface ListToChannelTableRecord {
    applicationStatus: string;
    applicationStatsName: string;
    id: number;
    providerId: number;
    firstName: string;
    lastName: string;
    email: string;
    photo: string;
    channelId: number;
    channelName: string;
    submitTime: string;
    status: string;
    statusName: string;
    assignee?: string;
    profileCompleteStatus?: string;
    archiveStatus?: 'Active' | 'Archived';
    providerPayPlanType: string;
    channelPayPlanType: string;
    product: EProduct;
}

export interface WithDrawChannelTableRecord {
    id: number;
    providerId: number;
    firstName: string;
    lastName: string;
    email: string;
    photo: string;
    channelId: number;
    channelName: string;
    submitTime: string;
    assignee?: string;
    providerPayPlanType: string;
    channelPayPlanType: string;
    listTime: null;
    assigneeUid: null;
    withdrawReason: string;
    status: string;
    statusName: string;
    delListTime?: string;
    archiveStatus?: EStatus;
}

export interface UpdateAssigneeInput {
    assignee: string;
    assigneeUid: number;
    channelId?: number;
    providerId: number;
}

export interface UpdateNotesInput {
    notes: string;
    channelId?: number;
    providerId: number;
}

export enum EChanelAuditStatus {
    UNIPROFILE_NEEDED = 'uniprofile-needed',
    UPLOAD_DATA = 'upload-data',
    WAITING_APPROVE = 'waiting-approve',
    FAILED = 'failed',
    APPROVED = 'approved',
}

export enum EChanelAuditStatusName {
    UNIPROFILE_NEEDED = 'Uniprofile Needed',
    UPLOAD_DATA = 'Upload data to Channel',
    WAITING_APPROVE = 'Waiting for Channels Approve',
    FAILED = 'Failed',
    APPROVED = 'Approved',
}

export enum EAuditStatus {
    FOLLOW_UP = 'follow-up',
    PENDING = 'pending',
    APPROVED = 'approved',
    REJECTED = 'rejected',
}

export enum EAuditStatusName {
    FOLLOW_UP = 'Need follow-up',
    PENDING = 'Pending approval',
    APPROVED = 'Approved',
    REJECTED = 'Disqualified',
}

export enum EUniprofileStatus {
    REQUIRED = 'required',
    UPDATE_IN_PROGRESS = 'update-in-progress',
    UNDER_REVIEW = 'under-review',
    UPDATE_SUBMITTED = 'update-submitted',
    APPROVED = 'approved',
    REJECTED = 'rejected',
}

export enum EUniprofileStatusName {
    REQUIRED = 'UniProfile not created',
    UPDATE_IN_PROGRESS = 'UniProfile in-progress',
    UNDER_REVIEW = 'UniProfile under review',
    UPDATE_SUBMITTED = 'Updates submitted',
    APPROVED = 'UniProfile approved',
    REJECTED = 'UniProfile disqualified',
}

export enum ESource {
    SCL = 'SCL',
    W = 'W',
    HEADWAY = 'headway',
}

export enum ESourceName {
    SCL = 'Contract portal',
    W = 'Website',
    headway = 'Headway landing page',
    REFERRAL = 'Referral',
}
export interface ProviderWithUniprofileStatus {
    id: number;
    firstName: string;
    lastName: string;
    email: string;
    createTime: string;
    submitTime: string;
    auditStatus: EAuditStatus;
    auditStatusName: EAuditStatusName;
    uniprofileStatus: EUniprofileStatus;
    uniprofileStatusName: EUniprofileStatusName;
    assignee: string;
    assigneeUid: number;
    verify: boolean;
    archiveStatus?: EStatus;
    source?: ESource;
    contract: string[];
    updateFlag?: boolean;
    onBoardingStep?: string;
    productsInterested?: string[];
    groupProvider?: boolean;
    credentialingFeeWaived?: boolean;
    // Insurance billing：Ops 已放行保留自有 clearinghouse 也可提交（ALLOW BILLING SUBMIT 列）
    billingSubmitAllowed?: boolean;
}

export interface GetOpsChannelWithdrawListInput {
    nameCond?: string;
    auditStatusList?: Array<CheckboxValueType>;
    applicationStatusList?: Array<CheckboxValueType>;
    orderKey?: string;
    orderType?: string;
    providerPayPlanTypeList?: Array<CheckboxValueType>;
    withdrawReasonList?: Array<CheckboxValueType>;
    channelPayPlanType?: Array<CheckboxValueType>;
    channelPayPlanTypeList?: Array<CheckboxValueType>;
    current: number;
    size: number;
}

export interface GetOpsChannelWithdrawDetailInput {
    id: number;
    channelId: number;
    providerId?: number;
}

export interface UpdateOpsChannelWithdrawStatusInput {
    id: number;
    channelId: number;
    providerId: number;
    passed: boolean;
}

export interface UpdateOpsChannelWithdrawNotesInput {
    notes: string;
    channelId: number;
    providerId: number;
    id: number;
}

export interface UpdateOpsChannelWithdrawAssigneeInput {
    assignee: string;
    assigneeUid: number;
    channelId?: number;
    providerId: number;
    id: number;
}

export type RoleItem = {
    id: number;
    name: string;
    providers: boolean;
    account: boolean;
    uniprofile: boolean;
    channels: boolean;
    channel_store: boolean;
    channel_listing: boolean;
    service_template: boolean;
    invite_member: boolean;
    add: boolean;
    super_edit: boolean;
    admin_edit: boolean;
    operator_edit: boolean;
    web_app_dev: boolean;
};

export interface IGetBookingLinkListResp {}

export interface ISaveHelloKlarityBookingLinkInput {
    bpProviderId: number;
    directBookingLink: string;
    id: number | null;
    specialty: string;
    state: string;
    serviceType: string;
}

export enum EOriderType {
    DESC = 'desc',
    ASC = 'asc',
}

export enum EGrowthTier {
    Starter = 'Starter',
    Growth = 'Growth',
    Scaling = 'Scaling',
}

export interface IGetProviderMappingDataInput {
    airtableProviderId?: string;
    bookLink?: number[];
    current?: number;
    ehrProviderId?: number;
    intakeqProviderId?: string;
    klarityEhrFlag?: boolean[];
    mappingStatus?: string[];
    name?: string;
    orderKey?: string;
    orderType?: EOriderType;
    patientRating?: number[];
    providerNameSlug?: string;
    size?: number;
    title?: string;
    type?: number;
    micStatus?: string[];
    ehrSetUpStatus?: boolean[];
    uidPublic?: string;
    programIdList?: number[];
    autoReschedule?: boolean;
    leadGenFeePerAppointment?: boolean;
    growthTier?: EGrowthTier[];
    gpStatus?: string[];
    gpFilter?: string[];
    hasGroupPractice?: boolean;
}

export enum EProviderListMappingStatusValue {
    PENDING_REVIEW = 'PR',
    LISTED = 'L',
    UNLISTED = 'U',
    DISQUALIFIED = 'D',
    UNIPROFILE_NEEDED = 'UN',
    PAUSE_LISTING = 'PL',
}

export enum EProviderMappingStatusValue {
    UNMAPPED = 'U',
    EHR_MAPPED = 'E',
    AIR_TABLE_MAPPING = 'A',
}

export interface IBPMappingItem {
    id: number;
    bpProviderId: number;
    ehrProviderId?: number;
    intakeqProviderId?: string;
    airtableProviderTitle?: string;
    airtableProviderId?: string;
    providerNameSlug: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    email: string;
    title?: string;
    listingStatus: EProviderListMappingStatusValue;
    mappingStatus: EProviderMappingStatusValue;
    profileLink?: string;
    bookLink: number;
    patientRating: number;
    lastUpdateAt?: string;
    conflict?: boolean;
    micStatus?: 'Effective' | 'None' | 'Expired';
    uniprofileStatus?: 'approved' | string;
    ehrSetUpCompleted?: boolean;
    w9Form?: string; //"provider/w_9/2024-06/5749b48b-edae-494f-b9f2-7d21f966ba9e.jpg",
    latestLaunchAt?: string;
    firstLaunchAt?: string;
    hasBookingLink?: boolean;
    turnOnSpecialtyList?: string[];
    turnOnAllSpecialty?: boolean;
    turnOffSpecialtyList?: string[];
    turnOffAllSpecialty?: boolean;
    autoReschedule?: boolean;
    leadGenFeePerAppointment?: boolean;
    klarityEhrFlag?: boolean;
    groupPractice?: { name: string; role: string; organizationId: number; organizationSlugName: string } | null;
}

export interface IProviderMappingDataResp {
    records?: IBPMappingItem[];
    total: number;
    size: number;
    current: number;
    orders: string[];
    optimizeCountSql: boolean;
    searchCount: boolean;
    countId?: number;
    maxLimit?: number;
    pages: number;
}

export interface IUpdateProviderSlugInput {
    id: number;
    providerNameSlug: string;
}

export interface IUpdatePatientRatingInput {
    id: number;
    patientRating: number;
}

export interface IUpdateProviderTitleInput {
    id: number;
    providerNameSlug: string;
}

export interface IUpdateHelloKlarityListingStatusInput {
    id: number;
    listingStatus: EProviderListMappingStatusValue;
}

export interface IUpdateHelloKlarityAirtableTitleInput {
    id: number;
    airtableProviderTitle: string;
}

export interface IUpdateHelloKlarityMappingStatusInput {
    id: number;
    mappingStatus: EProviderMappingStatusValue;
}

export interface IHelloKlarityBookingLink {
    bpProviderId: number;
    createAt?: string;
    createBy?: number;
    directBookingLink: string;
    id?: number;
    isVisible?: number;
    lastUpdateAt?: string;
    lastUpdateBy?: number;
    offerId: number;
    serviceType: string;
    specialty: string;
    state: string;
    bookingLinkName?: string;
    uuid?: string;
    conditions?: string | string[];
}
export interface ISaveSpecialtiesHighlightInput {
    providerId: number;
    specialtyHighlight: string;
}

export interface ISaveAirtableProviderInput {
    airtableProviderId: string;
    airtableProviderTitle: string;
    intakeqProviderId: string;
    listingStatus: EProviderListMappingStatusValue;
    providerNameSlug: string;
}

export enum EAppointmentType {
    VIDEO = 'Video Call',
    IN_PERSON = 'In Person',
}

export type TAppointment = {
    providerId?: string;
    ehrProviderId?: string;
    airtableProviderId?: string;
    intakeqProviderId?: string;
    serviceId: string;
    url?: string; //ehr return only
    offeringId: string;
    name: string;
    length: number; //min
    price: string;
    type: EAppointmentType[];
};

export interface IHelloKlarityViewPostMessageData {
    tabKey: string;
    hightlights: string[];
    appointmentList: TAppointment[];
    hasStorage: boolean;
    genericDraftData?: ISaveGenericInfoReqInput;
    specificDraftData?: ISaveSpecificInfoReqListInput;
}

export interface IGenerateSaleLinkInput {
    fullName: string;
    inviteEmail: string;
    phoneNumber: string;
    payPlanId: number;
    couponId?: string;
    promotionCode: string;
    message: string;
    onboardingCallBookLink: string;
    presentationEmbeddedLink: string;
}

export type TOpsUserInfo = {
    id: number;
    firstName: string;
    lastName: string;
    gmail: string;
    tel?: string;
    bookingLinkUrl?: string;
    title?: string;
    password: null;
    accessToken: null;
    role: string;
    permission: null;
    updateTime?: string;
};

export type TContentInfo = {
    id?: number;
    pageId?: number;
    title: string;
    content: string;
    imgUrl?: string;
    createAt: string; //'2024-05-20T12:34:01.000+00:00'
    createBy: number;
    updateAt: string; //'2024-05-20T12:34:01.000+00:00'
    updateBy: number;
    status: 'A' | 'D';
};

export enum EInternalLinkType {
    'SPECIALTY_DOCTOR' = 'specialty-doctor',
    'HEALTHY_CARE_SERVICE' = 'healthy-care-service',
    'FIND_DOCTOR' = 'find-doctor',
}

export type TInternalLink = {
    id?: number;
    type?: EInternalLinkType;
    linkTag: string;
    linkName?: string;
    linkUrl?: string;
    targetItem?: ServerMeta;
    pageId: number;
    seq: number;
    createAt?: string; //'2024-05-20T12:34:01.000+00:00'
    createBy?: number;
    updateAt?: string; //'2024-05-20T12:34:01.000+00:00'
    updateBy?: number;
};

export type TFaqInfo = {
    id?: number;
    pageId: number;
    question: string;
    answer?: string;
    answerCompnent?: React.ReactElement;
    seq: number;
    createAt?: string; //'2024-05-22T06:58:04.000+00:00',
    createBy?: number;
    updateAt?: string; //'2024-05-22T06:58:17.000+00:00',
    updateBy?: 43;
};

export interface IGetHelloKlarityPageManagementResp {
    name?: string[]; //condition,
    state?: string[]; // ["AC","CA"],
    type: string; //'STATE'|'CONDITION'|'HOME',
    statusList?: ('ACTIVE' | 'INACTIVE')[]; //'ACTIVE'|'INACTIVE',
    orderKey?: string; //'LAST_UPDATE_AT',
    orderType?: string; // 'DESC''ASC',
    lastUpdateByName?: string; // Filter by last updated by name
    size: number;
    current: number;
}

export interface IGeAddOnResp {
    providerName?: string;
    productCodeList?: string[];
    paymentStatusList?: ('Paid' | 'Failed')[]; //'ACTIVE'|'INACTIVE',
    size: number;
    current: number;
    orderKey?: string;
    orderType?: string;
}

export enum EProduct {
    KIWI = 'Kiwi',
    KLARITY = 'Klarity',
}

export interface IPerformanceInput {
    name?: string;
    current: number;
    size: number;
    orderField?: string;
    orderType?: string;
}

export type TPerformanceItem = {
    avgRating: number;
    bpProviderId: number;
    email: string;
    firstName: string;
    intakeqProviderId: string;
    lastName: string;
    latestReviewAt: string;
    providerSource: string;
    tags?: string[];
    totalReviewCount: number;
};

export interface IReviewInput {
    bpProviderId?: number;
    intakeqProviderId?: number;
    providerSource?: string;
    name?: string;
    publish?: boolean | string;
    needReply?: boolean | string;
    ratingFilterType?: string; //ONLY_5 ONLY_4  ONLY_3  ONLY_2 ONLY_1  GTE_3  LESS_THEN_3  NONE
    reviewDetail?: string;
    tag?: string;
    beginDate?: Date;
    endDate?: Date;
    patientEmail?: string;
    current?: number;
    size: number;
    orderField?: string; //replied_at submit_at
    orderType?: string; //asc desc
    disputeDecisionBy?: string;
    disputeStatusList?: EDISPUTE_STATUS[];
    disputeReasonList?: string[];
    disputeBeginDate?: Date;
    disputeEndDate?: Date;
}

export interface IReviewItem {
    id: number;
    bpProviderId?: number;
    intakeqProviderId?: number;
    providerSource?: string;
    name?: string;
    publish?: boolean | string;
    needReply?: boolean | string;
    ratingFilterType?: string; //ONLY_5 ONLY_4  ONLY_3  ONLY_2 ONLY_1  GTE_3  LESS_THEN_3  NONE
    reviewDetail?: string;
    tag?: string;
    beginDate?: string;
    endDate?: string;
    patientEmail?: string;
    email?: string;
}

export interface IReviewSummaryInput {
    providerId?: number | string;
    providerSource: string;
    summaryRange: string;
}

export interface IDisputeReviewInput {
    disputeStatusList?: EDISPUTE_STATUS[];
    disputeReasonList?: string[];
    disputeDecisionBy?: string;
    name?: string;
    ratingFilterType?: string; //ONLY_5 ONLY_4  ONLY_3  ONLY_2 ONLY_1  GTE_3  LESS_THEN_3  NONE
    reviewDetail?: string;
    beginDate?: Date;
    endDate?: Date;
    current?: number;
    size: number;
    orderField?: string; //replied_at submit_at
    orderType?: string; //asc desc
}

export interface IDisputeDissionSubmitInput {
    reviewId: number;
    reason: string;
    disputeStatus: EDISPUTE_STATUS;
}

export interface IUpdateReviewtToPrivateInput {
    reviewId: number;
    reason: string;
}
export interface TAssessment {
    id: number;
    name: string;
}

export interface IAssessmentInput {
    id?: string;
    assessmentName?: string;
    partnerName?: string;
    tags?: string[] | string;
    orderKey?: string;
    orderType?: string;
    size?: number;
    current?: number;
    status?: string;
    featureType?: string;
}

export type TAssessmentItem = {
    id?: number | string;
    uuid?: string | number;
    questionsFormLink?: string;
    questionsFormLinkId?: number | string;
    resultLink?: string;
    questionsFormResultLink?: string;
    updateAt?: string;
    assessmentName: string;
    partnerName?: string;
    conditionKeyTags?: string;
    tags?: string[];
    description?: string;
    finishMinute?: number;
    imgUrl?: string;
    iconUrl?: string;
    participantsNumber?: number;
    title?: string;
    fromType?: string;
    questionsNumber?: number;
    emailPrompt?: string;
    emailTemplateId?: number;
    promptManagement: string;
    analyzingPrompt?: string;
    status: string;
    featureType?: string;
    enableHelloKlarityDisplay?: boolean;
};

export type TEngagementQuestion = {
    id?: number;
    title: string;
    description?: string;
    type?: string;
    imgUrl?: string;
    mobileImgUrl?: string;
    seq?: number;
    assessmentId?: number | string;
    layout?: string;
};

export type TResult = {
    title: string;
    description: string;
    id?: string;
    riskLevel?: number;
    header?: string;
    imgUrl?: string;
    primaryCta?: string;
    primaryCtaUrl?: string;
    secondaryCta?: string;
    secondaryCtaUrl?: string;
    assessmentId?: number | string;
};

export type TPreviewResult = Partial<TResult> & {
    type: string;
};

export type TPreviewQuestion = {
    count?: number;
    questionsFormLink?: string;
    engagementQuestionCount?: number;
    questionsPreviewLink?: string;
} & Partial<
    TEngagementQuestion & {
        type: string;
    }
>;

export type TQuestionItem = {
    createAt?: string;
    createBy?: number;
    googleFormId?: number;
    id?: number;
    imgUrl?: string;
    options?: any[];
    questionId?: string;
    questionIdHex?: string;
    required?: boolean;
    title: string;
    titleHashCode?: string;
    type?: string;
    updateAt?: string;
    updateBy?: number;
    isFixed?: boolean;
    seq?: number;
};

export interface IAssessmentEmail {
    emailPrompt: string;
    riskLevel: number;
    subject: string;
    bannerImageUrl: string;
    ctaText: string;
    ctaUrl: string;
    assessmentId: number;
    emailTemplateId: number;
}

export interface IEmailListItem {
    content: string;
    id: number;
    title: string;
    type: string;
}

export interface ISaveGenericInfoReqInput {
    tags?: string[];
    medications?: {
        state: string;
        nonPrescribableMedications: string[];
        prescribableMedications: string[];
        syncAllState: boolean;
    }[];
}
export interface ISaveSpecificInfoReqListInput {
    condition?: string;
    headLines?: string;
    aboutMe?: string;
    tags?: string[];
    specialty?: string[];
    specialtyHighlight?: string[];
    conditionTreated?: string[];
}

export interface IProfileDataSaveInput {
    genericInfoReq: ISaveGenericInfoReqInput;
    specificInfoReqList: ISaveSpecificInfoReqListInput[];
}

// ================== ClosedFunnel 相关类型 ==================

export interface ICreateClosedFunnelInput {
    name: string;
    states: string;
    type: string;
    typeValues: string;
}

export interface IGetClosedFunnelListInput {
    name: string;
    states?: string[] | null;
    type: 'CONDITION' | 'MEDICATION';
    status?: Array<'A' | 'D'> | null;
    typeValues?: string[] | null;
    orderType: string;
    size: number;
    current: number;
}

export interface ICloseFunnelRow {
    createdAt: string;
    createdBy: string | null;
    firstTypeValue: string;
    id: number;
    isDeleted: boolean;
    link?: string | null;
    name: string;
    states: string;
    status: string;
    type: 'CONDITION' | 'MEDICATION';
    typeValues: string;
    updatedAt?: string;
    updatedBy?: string | null;
    uri: string;
    uuid: string;
}

export interface IClosedFunnelBasicInfo {
    id: number;
    uuid: string;
    uri: string;
    link: string;
    name: string;
    states: string; // 逗号分隔的州缩写
    type: string; // 如 'CONDITION'
    typeValues: string; // 逗号分隔的类型值
    firstTypeValue: string;
    status: 'A' | 'D'; // 'A'激活，'D'停用
    isDeleted: boolean;
    createdAt: string;
    createdBy: string | null;
    updatedAt: string;
    updatedBy: string | null;
}

export interface IClosedFunnelQuestion {
    id: number;
    funnelId: number;
    type: string; // 如 'STATE', 'PAYMENT', 'AVAILABILITY'
    typeName?: string; // 接口返回的类型展示名称，用于替代 type 的展示
    name: string; // 问题内容
    subtext: string; // 问题补充说明
    sort: number; // 排序
    status: 'A' | 'D';
    isDeleted: boolean;
    createdAt: string;
    createdBy: string | null;
    updatedAt: string;
    updatedBy: string | null;
    metaData?: string; // '{'showSelfPayPage':true}'
}

export enum EClosedFunnelFilterType {
    TREATMENT = 'TREATMENT',
    NON_PRESCRIBABLE_MEDICATION = 'NON_PRESCRIBABLE_MEDICATION',
    GROWTH_TIER = 'GROWTH_TIER',
}

export interface IClosedFunnelFilter {
    id: number;
    funnelId: number;
    filterType: EClosedFunnelFilterType; // 如 'TREATMENT'
    filterValues: string; // 逗号分隔的筛选值
    status: 'A' | 'D';
    isDeleted: boolean;
    createdAt: string;
    createdBy: string | null;
    updatedAt: string;
    updatedBy: string | null;
}

export interface IClosedFunnelDetail {
    basicInfo: IClosedFunnelBasicInfo;
    questions: IClosedFunnelQuestion[];
    filters: IClosedFunnelFilter[];
}

export interface IClosedFunnelQuestionUpdateInput {
    name: string;
    subtext: string;
    status: 'A' | 'D';
}

export interface IMedication {
    id: number;
    pageId: number;
    specialtyId: number;
    medicationPageUrl: string;
    imageUrl: string;
    subtext: string;
    tag: string;
    seq: number;
    title: string;
}

export interface ISpecialty {
    createdAt: string;
    createdBy: number;
    id: number;
    pageId: number;
    specialtyName: string;
    specialtyPriority: number | null;
    status: 'A' | 'D';
    updatedAt: string;
    updatedBy: number;
    medications?: IMedication[];
}

export interface IUpdateKlaritySpecialtyInHPInput {
    id?: number;
    pageId: number;
    specialtyName: string;
    status: 'A' | 'D';
    specialtyPriority?: number | null;
}

// State Management Types
export interface TStateItem {
    id: string;
    state: string;
    status: 'ACTIVE' | 'INACTIVE';
    lastModified: string;
    lastModifiedBy?: string;
}

export interface IStateManagementInput {
    current?: number;
    size?: number;
    state?: string;
    status?: string;
    orderKey?: string;
    orderType?: string;
}

export interface IStateManagementResponse {
    data: {
        records: TStateItem[];
        total: number;
        current: number;
        size: number;
    };
    success: boolean;
    error?: string;
}

export interface IUpdateStateStatusInput {
    id: string;
    status: 'ACTIVE' | 'INACTIVE';
}

// Group Practice

export enum EGPListingStatus {
    INIT = 'INIT',
    LISTED = 'LISTED',
    PAUSE_LISTED = 'PAUSE_LISTED',
    UNLISTED = 'UNLISTED',
}

export interface IGPPracticeItem {
    id: number;
    name: string;
    state: string;
    slug: string;
    listingStatus: EGPListingStatus | null;
    providers: number;
    patients: number | null;
    reassigns30d: number | null;
    admin: string;
    adminEmail: string;
    submittedDate: string | null;
    klarityLaunchDate: string | null;
}

export interface IGPPracticesResp {
    records: IGPPracticeItem[];
    total: number;
}

export interface IGPDetailInsurance {
    carrier: string;
    plans: string[];
    state: string;
    insuranceType: string;
}

export interface IGPDetail {
    organizationId: number;
    slugName: string;
    listingStatus: string;
    redirect: boolean;
    name: string;
    ownerProviderId: number;
    headLine: string | null;
    intro: string | null;
    photo: string | null;
    photoList: string[] | null;
    videoList: string[];
    specialtyList: string[];
    rating: number;
    ratingCount: number;
    reviewCount: number;
    address: string | null;
    insurances: IGPDetailInsurance[];
    listedProviderCount: number;
}

// HVA

export interface IHVAAssignedProviders {
    all: boolean;
    providerIds: number[] | null;
}

export interface IHVAOtherPractice {
    orgId: number;
    memberId?: number;
    gpName: string;
    roleId: number;
    roleName: string;
    isAdmin: boolean;
}

export interface IHVAItem {
    userId: number;
    name: string;
    email: string;
    orgId: number;
    gpName: string;
    roleId: number;
    roleName: string;
    isAdmin: boolean;
    status: 'ACTIVE' | 'PENDING' | 'DISABLED';
    loginStatus: string;
    lastActiveAt: string | null;
    practicesCount: number;
    assignedProviders: IHVAAssignedProviders | null;
    otherPractices: IHVAOtherPractice[] | null;
}

export interface IStaffListItem {
    memberId: number;
    userId: number;
    name: string;
    email: string;
    orgId: number;
    gpName: string;
    roleId: number;
    roleName: string;
    isAdmin: boolean;
    status: 'ACTIVE' | 'PENDING' | 'DISABLED';
    lastActiveAt: string | null;
    practicesCount: number;
    assignedProviders: IHVAAssignedProviders | null;
    otherPractices: IHVAOtherPractice[] | null;
}

// Staff / Team Management

export interface ITeamMemberAssignedProviders {
    all: boolean;
    providerIds: number[] | null;
}

export interface ITeamMember {
    id: number;
    userId: number;
    firstName: string | null;
    lastName: string | null;
    email: string;
    phoneNumber: string | null;
    roleId: number;
    roleCode: string;
    isOwner: boolean;
    isAdmin: boolean;
    isDefault: boolean;
    status: 'PENDING' | 'ACTIVE' | 'DISABLED';
    portalStatus: 'PENDING' | 'SIGNED_UP' | 'ONBOARDING_COMPLETED';
    lastActiveAt: string | null;
    providerId?: number;
    assignedProviders: ITeamMemberAssignedProvider[];
    isAllProviders: boolean;
    listingStatus: string | null;
    listingStatusChangeAt: string | null;
    listingReactivateAt: string | null;
}

export interface ITeamMemberAssignedProvider {
    providerId: number;
    firstName: string | null;
    lastName: string | null;
}

// Staff Detail

export interface IStaffMembership {
    memberId: number;
    orgId: number;
    gpName: string;
    ownerProviderId: number | null;
    ownerProviderName: string | null;
    roleId: number;
    roleName: string;
    isAdmin: boolean;
    status: 'PENDING' | 'ACTIVE' | 'DISABLED';
    invitedBy: string | null;
    invitedByName: string | null;
    joinedDate: string | null;
    lastActiveAt: string | null;
    assignedProviders: ITeamMemberAssignedProviders | null;
}

export interface IStaffDetail {
    userId: number;
    name: string;
    email: string;
    phone: string | null;
    status: 'ACTIVE' | 'PENDING' | 'DISABLED';
    loginStatus?: string;
    practicesCount: number;
    isHva: number;
    notes?: string;
    assignee?: string;
    memberships: IStaffMembership[];
}
